import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi, errMsg } from "@/lib/api";
import { RETURN_STATUS } from "@/components/admin/OrderExtras";

const RETURN_DAYS = 15;

const canReturn = (o) => {
  if (o.payment_status !== "paid" || o.status === "cancelado") return false;
  if (o.return_request && o.return_request.status !== "rechazada") return false;
  const days = (Date.now() - new Date(o.paid_at || o.created_at).getTime()) / 86400000;
  return days <= RETURN_DAYS + 5;
};

const ReturnForm = ({ order, onClose, onDone }) => {
  const call = useApi();
  const [qty, setQty] = useState(order.items.map(() => 0));
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState("tienda");
  const [wants, setWants] = useState("reembolso");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    const items = qty.map((q, index) => ({ index, quantity: q })).filter((i) => i.quantity > 0);
    if (!items.length) return toast.error("Elige al menos un artículo");
    setBusy(true);
    try {
      await call("post", `/orders/${order.id}/return-request`, { items, reason, method, wants, exchange_note: note });
      toast.success("Solicitud enviada. Te hemos mandado un email con los pasos");
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="return-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-black uppercase">Devolver · {order.number}</DialogTitle></DialogHeader>
        <form onSubmit={send} className="space-y-4 text-sm">
          <div className="space-y-2">
            {order.items.map((i, k) => (
              <div key={k} className="flex items-center gap-3">
                <span className="flex-1">{i.name} <span className="text-slate-500">· T. {i.size}{i.color && ` · ${i.color}`}</span></span>
                <select value={qty[k]} onChange={(e) => setQty(qty.map((q, j) => (j === k ? Number(e.target.value) : q)))} className="h-9 border rounded-full px-2" data-testid={`return-qty-${k}`}>
                  {Array.from({ length: i.quantity + 1 }, (_, n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            {[["reembolso", "Quiero el reembolso"], ["cambio", "Quiero un cambio"]].map(([v, l]) => (
              <button type="button" key={v} onClick={() => setWants(v)} data-testid={`return-wants-${v}`}
                className={`flex-1 h-10 rounded-full border font-semibold ${wants === v ? "bg-brand text-white border-brand" : ""}`}>{l}</button>
            ))}
          </div>
          {wants === "cambio" && <input className="field" placeholder="¿Por qué talla o color lo cambias?" value={note} onChange={(e) => setNote(e.target.value)} data-testid="return-exchange-note" />}
          <textarea required minLength={3} className="field h-20 py-2" placeholder="Motivo (ej. me queda pequeño)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="return-reason" />
          <div className="space-y-2">
            <label className="flex gap-2"><input type="radio" checked={method === "tienda"} onChange={() => setMethod("tienda")} data-testid="return-method-tienda" /> En la tienda de Olula del Río (gratis)</label>
            <label className="flex gap-2"><input type="radio" checked={method === "mensajeria"} onChange={() => setMethod("mensajeria")} data-testid="return-method-mensajeria" /> Por mensajería o correo (envío a mi cargo)</label>
          </div>
          <p className="text-xs text-slate-500">Consulta la <Link to="/devoluciones" className="underline">política de devoluciones</Link>.</p>
          <button disabled={busy} className="btn-primary w-full" data-testid="return-submit">{busy ? "Enviando…" : "Enviar solicitud"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const OrderReturn = ({ order, onDone }) => {
  const [open, setOpen] = useState(false);
  const r = order.return_request;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {r && <span className={`tag ${RETURN_STATUS[r.status]?.cls}`} data-testid={`order-return-status-${order.number}`}>{RETURN_STATUS[r.status]?.label}</span>}
      {order.tracking && <span className="text-xs text-slate-600" data-testid={`order-tracking-${order.number}`}>Seguimiento: {order.carrier} {order.tracking}</span>}
      {canReturn(order) && (
        <button onClick={() => setOpen(true)} className="ml-auto text-xs font-semibold uppercase tracking-wide flex items-center gap-1 hover:text-brand" data-testid={`order-return-button-${order.number}`}>
          <Undo2 className="w-4 h-4" /> Solicitar devolución
        </button>
      )}
      {open && <ReturnForm order={order} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone(); }} />}
    </div>
  );
};
